import { Scroll, Heart, Shield, FileText, Users, House, Stamp } from '@phosphor-icons/react';
import { ServiceDetailLayout } from '../../components/ServiceDetailLayout';
import { SERVICES_INDEX, SERVICE_HERO_IMAGES } from '../../lib/servicesIndex';

/**
 * Estate Planning page — special "7 essential documents" block per client request.
 * Trust Package + Healthcare Directive headlined, plus 5 supporting documents.
 */
const ESTATE_DOCUMENTS = [
  {
    icon: Shield,
    name: 'Revocable Living Trust',
    headline: 'The Trust Package',
    description:
      'The cornerstone of a modern estate plan — keeps your assets out of probate, private from the public record, and seamlessly transferable to the people you love.',
    badge: 'Centerpiece',
  },
  {
    icon: Heart,
    name: 'Advance Healthcare Directive',
    headline: 'Healthcare Directive',
    description:
      'Tells your doctors and family how you\'d want to be cared for if you couldn\'t speak for yourself. Includes living-will instructions and end-of-life preferences.',
    badge: 'Essential',
  },
  {
    icon: Users,
    name: 'Healthcare Power of Attorney',
    headline: 'Healthcare POA',
    description:
      'Names a trusted person to make medical decisions on your behalf. Pairs hand-in-glove with the Healthcare Directive.',
  },
  {
    icon: Stamp,
    name: 'Durable Power of Attorney',
    headline: 'Financial POA',
    description:
      'Authorizes a trusted agent to handle banking, taxes, real estate, and bills if you\'re ever incapacitated — keeping your life running while you recover.',
  },
  {
    icon: Scroll,
    name: 'Last Will & Testament',
    headline: 'Pour-Over Will',
    description:
      'Captures any assets that aren\'t already inside the trust and directs them in. Names guardians for minor children — non-negotiable for parents.',
  },
  {
    icon: FileText,
    name: 'HIPAA Authorization',
    headline: 'HIPAA Release',
    description:
      'Lets named loved ones access your medical records and speak with your providers — eliminates the most common hospital-bedside frustration families face.',
  },
  {
    icon: House,
    name: 'Trust Funding Documents',
    headline: 'Deed Transfers & Assignments',
    description:
      'The often-overlooked step that actually puts your home, accounts, and assets INTO the trust. India coordinates the deed re-recording so the trust isn\'t just paper.',
  },
];

const EstateDocumentsBlock = () => (
  <section data-testid="estate-documents-section" className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 max-w-2xl mx-auto">
        <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
          The Estate Planning Suite
        </p>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] mb-4 leading-tight">
          The 7 documents <span className="italic">every Tennessee family</span> should have notarized.
        </h2>
        <p className="text-[#5C5C5C] text-base sm:text-lg leading-relaxed">
          India regularly notarizes the full estate-planning packet for Nashville-area attorneys,
          financial planners, and families putting their affairs in order — at home, at the office,
          or bedside.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {ESTATE_DOCUMENTS.map((doc, i) => (
          <article
            key={i}
            data-testid={`estate-document-${i}`}
            className={`relative bg-[#FBF8F1] border rounded-sm p-7 transition-all hover:shadow-xl hover:-translate-y-0.5 ${
              doc.badge ? 'border-[#D4AF37]/60 lg:col-span-1' : 'border-[#E8E2D5] hover:border-[#800000]/30'
            }`}
          >
            {doc.badge && (
              <span className="absolute -top-3 left-6 bg-[#D4AF37] text-[#1A0000] text-[9px] font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-sm">
                {doc.badge}
              </span>
            )}

            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#800000]/8 flex items-center justify-center flex-shrink-0">
                <doc.icon weight="duotone" size={24} className="text-[#800000]" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-1">
                  Document {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="font-serif-display text-xl font-semibold text-[#800000] leading-tight">
                  {doc.headline}
                </h3>
                <p className="text-xs text-[#5C5C5C] mt-0.5 italic">{doc.name}</p>
              </div>
            </div>

            <p className="text-[#3C3C3C] text-sm leading-[1.75]">
              {doc.description}
            </p>
          </article>
        ))}
      </div>

      <div className="mt-14 max-w-3xl mx-auto bg-[#FBF8F1] border-l-4 border-[#D4AF37] p-7 sm:p-8">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#800000] font-bold mb-3">
          A Note from India
        </p>
        <p className="font-serif-display italic text-lg sm:text-xl text-[#1A0000] leading-relaxed">
          "I work directly with your attorney or financial planner if you have one — and if you don't,
          I'll point you to one I trust. Either way, your packet leaves my hands properly executed,
          dated, and ready to be filed."
        </p>
        <p className="text-xs text-[#5C5C5C] mt-4 tracking-[0.2em] uppercase">
          — India Scruggs · MBA · Tennessee Notary Public
        </p>
      </div>
    </div>
  </section>
);

export const EstatePlanningPage = () => (
  <ServiceDetailLayout
    eyebrow="Estate Planning Documents"
    title="Trust packages, healthcare directives,"
    titleAccent="and everything in between."
    subtitle="A discreet, MBA-led mobile notary trusted by Nashville-area attorneys, financial planners, and families to handle the seven documents that matter most — at home, at the office, or at the hospital bedside."
    heroImage={SERVICE_HERO_IMAGES['estate-planning']}
    intro={{
      kicker: 'Estate & Power of Attorney',
      heading: 'White-glove handling of life\'s most important paperwork.',
      body: [
        'Estate planning documents aren\'t routine paperwork — they\'re instructions for the hardest moments your family will ever face. India brings the same precision she learned in corporate boardrooms to your kitchen table, your attorney\'s office, or a quiet hospital room.',
        'Every document is reviewed for proper execution, every witness requirement is met, and every signature is recorded with the patience and discretion the moment deserves. If your loved one is on hospice or in palliative care, India will come bedside — same day, no questions asked.',
        'And because she\'s a Tennessee Notary Public with corporate-grade attention to detail, your trust package, healthcare directive, and POA package leaves her hands ready to be filed, recorded, or stored without a single follow-up call.',
      ],
      pullQuote: {
        text: 'India sat with us at my mother\'s bedside for two hours and never once made it feel rushed. Every page handled with grace.',
        attribution: 'Estate Client · Davidson County',
      },
    }}
    bullets={[
      { title: 'Hospital, Hospice & Bedside Visits', description: 'Vanderbilt, Saint Thomas, TriStar, Alive Hospice, and senior-living communities throughout Nashville.' },
      { title: 'Attorney & Financial-Planner Coordination', description: 'India works directly with your legal or financial team to execute the packet exactly as drafted.' },
      { title: 'Witness Coordination', description: 'Tennessee requires two qualified witnesses for many estate documents — India brings them or coordinates yours.' },
      { title: 'Same-Day & Emergency Visits', description: 'When the situation can\'t wait, India answers the phone and shows up — including evenings and weekends.' },
      { title: 'Discreet & Confidential', description: 'Your documents, your story, and your family\'s privacy stay protected. Always.' },
      { title: 'Trust Funding Follow-Through', description: 'India can also notarize the deed transfers and account assignments that actually fund the trust.' },
    ]}
    extraSection={<EstateDocumentsBlock />}
    faqs={[
      { q: 'Do I need an attorney before I call you?', a: 'Not always. Many trust packages and healthcare directives are drafted by attorneys, but plenty of clients use reputable online services or estate-planning software. India will notarize either — and if you don\'t have a lawyer, she can refer you to a few she trusts.' },
      { q: 'Can you come to a hospital or hospice facility?', a: 'Absolutely. India regularly visits Vanderbilt, Saint Thomas, TriStar, Alive Hospice, and senior-living communities throughout Middle Tennessee. Bedside notarization is one of her most-requested services.' },
      { q: 'What\'s the difference between a will and a trust?', a: 'A will directs how your assets are distributed after death (and goes through public probate). A trust holds your assets while you\'re alive and transfers them privately, often avoiding probate entirely. Most modern estate plans use both — the trust as the centerpiece, the will as a "pour-over" backup.' },
      { q: 'Can a healthcare directive be notarized in Tennessee?', a: 'Yes — Tennessee law allows healthcare directives to be either notarized OR witnessed by two qualified adults. Many families prefer notarization for the added durability and out-of-state recognition.' },
      { q: 'How do I know if my trust is "funded"?', a: 'A trust is only as good as the assets actually placed inside it. If your home isn\'t deeded to the trust and your accounts aren\'t retitled, the trust can\'t protect them. India can notarize the deed-transfer paperwork and walk you through the funding checklist.' },
    ]}
    relatedSlugs={['general-notary', 'apostille', 'remote-online-notarization']}
    servicesIndex={SERVICES_INDEX}
  />
);
